import React from "react";
import { withTheme } from "styled-components";
import { OrbitTheme } from "../../../themes/architecture/orbit-theme.interface";
import { SvgSizeUnit } from "../../icon/svg-icon/svg.icon";
import { IButtonCommonProps, ButtonPreset, ButtonStroke, ButtonSizeVariant, IButtonIconProps } from "./button-props.interface";
import { ButtonBox } from "./boxes/button.box";
import { ButtonLabelBox } from "./boxes/button-label.box";
import { ButtonIconWrapperBox } from "./boxes/button-icon-wrapper.box";

interface IButtonProps extends IButtonCommonProps {
    preset: ButtonPreset;
    stroke?: ButtonStroke;
    icon?: IButtonIconProps;
    sizeVariant?: ButtonSizeVariant;
    className?: string;
    allowLabelWrap?: boolean;
    tooltip?: string;

    /**
     * theme is addded by styled-component's withTheme
     * children should not be here, but typescript complains otherwise, when using this component with children
     */
    theme: OrbitTheme;
    children?: React.ReactNode;

    /* button interactions */
    onClick?(): void;
    onMouseEnter?(): void;
    onMouseLeave?(): void;
}

class $Button extends React.Component<IButtonProps> {

    private _renderContent(fontSize: number, lateralPadding: number, allowLabelWrap?: boolean) {
        if(!this.props.children) {
            return null;
        }

        return (
            <ButtonLabelBox
                fontSize={fontSize}
                lateralPadding={lateralPadding}
                allowLabelWrap={allowLabelWrap}>
                {this.props.children}
            </ButtonLabelBox>
        );
    }


    render() {
        let {
            children, theme, preset, icon, stroke, sizeVariant, rotated45, rounded, elevated, allowLabelWrap, tooltip,
            onClick, onMouseEnter, onMouseLeave,
            ...otherProps
        } = this.props;
        let hasIcon = !!icon;
        let hasLabel = !!children;

        onClick = this.props.disabled ? undefined : onClick;
        onMouseEnter = this.props.disabled ? undefined : onMouseEnter;
        onMouseLeave = this.props.disabled ? undefined : onMouseLeave;

        let presetValues = theme.baseUIComponents.button.presets[preset];
        let themePresetDefaults = theme.baseUIComponents.button.defaults;

        stroke = stroke ?? presetValues.stroke ?? themePresetDefaults.stroke;
        rounded = rounded ?? presetValues.rounded ?? themePresetDefaults.rounded;
        elevated = elevated ?? presetValues.elevated ?? themePresetDefaults.elevated;
        sizeVariant = sizeVariant || "regular";

        let iconSize = icon && icon.size ? icon.size : "regular";
        let iconPlacement = icon && icon.placement ? icon.placement : "left";

        let strokeWidth = theme.baseUIComponents.button.strokeValues[stroke];
        let height = theme.baseUIComponents.button.sizeVariantValues[sizeVariant].height;
        let iconSizeValue = theme.baseUIComponents.button.sizeVariantValues[sizeVariant].iconSize[iconSize];
        let fontSize = theme.baseUIComponents.button.sizeVariantValues[sizeVariant].fontSize || presetValues.fontSize || themePresetDefaults.fontSize;
        let lateralPadding = sizeVariant === "small" ? 0.6 : 1;

        /**
         * Rotation is supported for icon only buttons. We support this for casino 777 play game button which is a diamond shaped button
         */
        rotated45 = rotated45 && hasIcon && !hasLabel;

        return <ButtonBox
            colorSet={presetValues.colors}
            hasLabel={hasLabel} hasIcon={hasIcon}
            strokeWidth={strokeWidth} height={height} iconSize={iconSizeValue} rotated45={rotated45}
            lateralPadding={lateralPadding}
            rounded={rounded} elevated={elevated}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            title={tooltip}
            {...otherProps}
        >
            { (icon && iconPlacement === "left") ? this.renderIcon(icon.Component, iconSizeValue, {rotated45, reverseOnRTL: icon.reverseOnRTL}) : null }
            { this._renderContent(fontSize, lateralPadding, allowLabelWrap)}
            { (icon && iconPlacement === "right") ? this.renderIcon(icon.Component, iconSizeValue, {rotated45, reverseOnRTL: icon.reverseOnRTL}) : null }
        </ButtonBox>
    }

    private renderIcon(IconComponent: IButtonIconProps["Component"], size: number, options?: {rotated45?: boolean, reverseOnRTL?: boolean}) {

        if(!IconComponent) {
            return null;
        }

        return <ButtonIconWrapperBox rotated45={options?.rotated45} reverseOnRTL={options?.reverseOnRTL}>
            <IconComponent sizeUnit={SvgSizeUnit.Em} size={size} />
        </ButtonIconWrapperBox>


    }

}

export const Button = withTheme($Button);
